import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Assignment,
  AssignmentSubmission,
  Attendance,
  Class,
  Quiz,
  QuizAttempt,
  Student,
  Teacher,
} from "../models";
import {
  calculatePercentage,
  formatPercentage,
  percentageToLetterGrade,
} from "../utils/grade-utils";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class ProgressReportController {
  /**
   * Get Student Progress Report (Student View)
   * @route GET /api/v1/student/progress-report
   * @access Private (Student)
   */
  async getStudentProgressReport(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { classId, semesterId } = req.query;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      // Find student
      const student = await Student.findOne({
        userId: mongoose.Types.ObjectId.createFromHexString(userId),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        isApproved: true,
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found or not approved",
        } as APIResponse);
        return;
      }

      // Get student's classes
      const studentId: mongoose.Types.ObjectId =
        student._id instanceof mongoose.Types.ObjectId
          ? student._id
          : mongoose.Types.ObjectId.createFromHexString(String(student._id));
      const classQuery: any = {
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        studentIds: studentId,
      };

      if (classId) {
        classQuery._id = new mongoose.Types.ObjectId(classId as string);
      }

      if (semesterId) {
        classQuery.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      }

      const classes = await Class.find({ ...classQuery })
        .populate("semester", "semesterName academicYear")
        .populate("teacherIds", "fullName employeeId");

      const progressData = [];

      for (const classItem of classes) {
        const classDoc = classItem as any;

        // Get assignments for this class
        const assignments = await Assignment.find({
          classId: classItem._id,
          status: "published",
        });

        const assignmentProgress = [];
        let totalAssignmentMarks = 0;
        let obtainedAssignmentMarks = 0;
        let completedAssignments = 0;
        let lateSubmissions = 0;

        for (const assignment of assignments) {
          const submission = await AssignmentSubmission.findOne({
            assignmentId: assignment._id,
            studentId: student._id,
          });

          totalAssignmentMarks += assignment.totalMarks;
          if (submission) {
            completedAssignments++;
            if (submission.isLateSubmission) {
              lateSubmissions++;
            }
            if (submission.marksObtained !== undefined) {
              obtainedAssignmentMarks += submission.marksObtained;
            }
          }

          // Calculate percentage and grade
          const percentage =
            submission?.marksObtained === undefined
              ? 0
              : calculatePercentage(
                  submission.marksObtained,
                  assignment.totalMarks,
                );
          const letterGrade =
            submission?.marksObtained === undefined
              ? null
              : percentageToLetterGrade(percentage, true);

          assignmentProgress.push({
            id: assignment.id,
            title: assignment.title,
            totalMarks: assignment.totalMarks,
            deadline: assignment.deadline,
            submitted: !!submission,
            submittedAt: submission?.submittedAt,
            isLateSubmission: submission?.isLateSubmission || false,
            marksObtained: submission?.marksObtained,
            percentage:
              submission?.marksObtained === undefined ? null : percentage,
            letterGrade,
            status: submission?.status,
            graded: submission?.isGraded() || false,
          });
        }

        // Get quizzes for this class
        const quizzes = await Quiz.find({
          classId: classItem._id,
          status: "published",
        });

        const quizProgress = [];
        let totalQuizMarks = 0;
        let obtainedQuizMarks = 0;
        let completedQuizzes = 0;

        for (const quiz of quizzes) {
          const attempts = await QuizAttempt.find({
            quizId: quiz._id,
            studentId: student._id,
            status: { $in: ["submitted", "graded"] },
          }).sort({ submittedAt: -1 });

          totalQuizMarks += quiz.totalMarks;
          if (attempts.length > 0) {
            completedQuizzes++;
            const bestAttempt = attempts[0];
            if (bestAttempt.totalMarksObtained !== undefined) {
              obtainedQuizMarks += bestAttempt.totalMarksObtained;
            }
          }

          // Calculate percentage
          const bestScore = attempts[0]?.totalMarksObtained;
          const percentage =
            bestScore === undefined
              ? 0
              : calculatePercentage(bestScore, quiz.totalMarks);

          quizProgress.push({
            id: quiz.id,
            title: quiz.title,
            totalMarks: quiz.totalMarks,
            deadline: quiz.deadline,
            attempts: attempts.length,
            bestScore,
            percentage: bestScore === undefined ? null : percentage,
            status: attempts[0]?.status,
            graded: attempts[0]?.isGraded() || false,
          });
        }

        // Get attendance for this class
        const attendanceStats = await Attendance.getStatistics(
          classItem._id,
          studentId,
        );

        // Calculate overall grade
        const totalMarks = totalAssignmentMarks + totalQuizMarks;
        const obtainedMarks = obtainedAssignmentMarks + obtainedQuizMarks;
        const overallGradePercentage =
          totalMarks > 0 ? (obtainedMarks / totalMarks) * 100 : 0;
        const overallLetterGrade =
          overallGradePercentage > 0
            ? percentageToLetterGrade(overallGradePercentage, true)
            : null;

        // Calculate assignment average
        const assignmentAveragePercentage =
          completedAssignments > 0 && totalAssignmentMarks > 0
            ? (obtainedAssignmentMarks / totalAssignmentMarks) * 100
            : 0;

        // Calculate quiz average
        const quizAveragePercentage =
          completedQuizzes > 0 && totalQuizMarks > 0
            ? (obtainedQuizMarks / totalQuizMarks) * 100
            : 0;

        // Get primary teacher name
        const teachers = classDoc.teacherIds || [];
        const primaryTeacher =
          Array.isArray(teachers) && teachers.length > 0 ? teachers[0] : null;
        const teacherName =
          primaryTeacher &&
          typeof primaryTeacher === "object" &&
          "fullName" in primaryTeacher
            ? primaryTeacher.fullName
            : "N/A";

        progressData.push({
          class: {
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            courseName: classItem.courseName,
            courseCode: classItem.courseCode || classItem.classCode,
            teacherName,
            semester: classDoc.semester
              ? {
                  id: classDoc.semester.id || classDoc.semester._id,
                  semesterName: classDoc.semester.semesterName,
                  academicYear: classDoc.semester.academicYear,
                }
              : null,
            teachers: Array.isArray(classDoc.teacherIds)
              ? classDoc.teacherIds
              : [],
          },
          assignments: {
            total: assignments.length,
            completed: completedAssignments,
            lateSubmissions,
            progress: assignmentProgress,
            averageGrade: assignmentAveragePercentage,
            averageGradeFormatted: formatPercentage(
              assignmentAveragePercentage,
            ),
          },
          quizzes: {
            total: quizzes.length,
            completed: completedQuizzes,
            progress: quizProgress,
            averageGrade: quizAveragePercentage,
            averageGradeFormatted: formatPercentage(quizAveragePercentage),
          },
          attendance: {
            total: attendanceStats.total,
            present: attendanceStats.present,
            absent: attendanceStats.absent,
            late: attendanceStats.late,
            presentPercentage: attendanceStats.presentPercentage || "0.00",
            absentPercentage: attendanceStats.absentPercentage || "0.00",
            attendancePercentage: attendanceStats.presentPercentage
              ? Number.parseFloat(attendanceStats.presentPercentage)
              : 0,
          },
          overall: {
            totalMarks,
            obtainedMarks,
            overallGrade: overallGradePercentage.toFixed(2),
            overallGradeFormatted: formatPercentage(overallGradePercentage),
            letterGrade: overallLetterGrade,
            completionRate:
              assignments.length + quizzes.length > 0
                ? ((completedAssignments + completedQuizzes) /
                    (assignments.length + quizzes.length)) *
                  100
                : 0,
            classPerformance: overallGradePercentage, // For progress bar
          },
        });
      }

      // Calculate overall statistics
      const overallStats = {
        totalClasses: classes.length,
        totalAssignments: progressData.reduce(
          (sum, p) => sum + p.assignments.total,
          0,
        ),
        completedAssignments: progressData.reduce(
          (sum, p) => sum + p.assignments.completed,
          0,
        ),
        totalQuizzes: progressData.reduce((sum, p) => sum + p.quizzes.total, 0),
        completedQuizzes: progressData.reduce(
          (sum, p) => sum + p.quizzes.completed,
          0,
        ),
        averageGrade:
          progressData.length > 0
            ? progressData.reduce(
                (sum, p) => sum + Number.parseFloat(p.overall.overallGrade),
                0,
              ) / progressData.length
            : 0,
      };

      res.status(200).json({
        success: true,
        message: "Progress report retrieved successfully",
        data: {
          student: {
            id: student.id,
            fullName: student.fullName,
            studentId: student.studentId,
            academicLevel: student.academicLevel,
            academicYear: student.academicYear,
          },
          progress: progressData,
          overallStatistics: overallStats,
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get student progress report error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve progress report",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get Student Progress Report (Teacher View)
   * @route GET /api/v1/school/students/:studentId/progress-report
   * @access Private (Teacher, School Admin)
   */
  async getStudentProgressReportForTeacher(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { studentId } = req.params;
      const { classId, semesterId } = req.query;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const userRole = req.user?.role;

      if (!userId || !schoolId) {
        res.status(400).json({
          success: false,
          message: "User ID and School ID are required",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(studentId)) {
        res.status(400).json({
          success: false,
          message: "Invalid student ID",
        } as APIResponse);
        return;
      }

      const studentObjectId: mongoose.Types.ObjectId =
        mongoose.Types.ObjectId.createFromHexString(studentId);

      // Find student
      const student = await Student.findOne({
        _id: studentObjectId,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      }).populate("userId", "email");

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
        } as APIResponse);
        return;
      }

      // If teacher, verify they have access to this student
      if (userRole === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
          schoolId: new mongoose.Types.ObjectId(schoolId),
        });

        if (!teacher) {
          res.status(404).json({
            success: false,
            message: "Teacher profile not found",
          } as APIResponse);
          return;
        }

        // Check if teacher has any classes with this student
        const teacherClasses = await Class.find({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          teacherIds: teacher._id,
          studentIds: student._id,
        });

        if (teacherClasses.length === 0) {
          res.status(403).json({
            success: false,
            message: "You don't have access to this student's progress",
          } as APIResponse);
          return;
        }
      }

      // Get student's classes
      const classQuery: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
        studentIds: student._id,
      };

      if (classId) {
        classQuery._id = new mongoose.Types.ObjectId(classId as string);
      }

      if (semesterId) {
        classQuery.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      }

      // If teacher, filter to only their classes
      if (userRole === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
          schoolId: new mongoose.Types.ObjectId(schoolId),
        });
        if (teacher) {
          classQuery.teacherIds = teacher._id;
        }
      }

      const classes = await Class.find({ ...classQuery })
        .populate("semester", "semesterName academicYear")
        .populate("teacherIds", "fullName employeeId");

      const progressData = [];

      for (const classItem of classes) {
        const classDoc = classItem as any;

        // Get assignments for this class
        const assignments = await Assignment.find({
          classId: classItem._id,
          status: "published",
        });

        const assignmentProgress = [];
        let totalAssignmentMarks = 0;
        let obtainedAssignmentMarks = 0;
        let completedAssignments = 0;
        let lateSubmissions = 0;

        for (const assignment of assignments) {
          const submission = await AssignmentSubmission.findOne({
            assignmentId: assignment._id,
            studentId: student._id,
          }).populate("gradedBy", "fullName employeeId");

          totalAssignmentMarks += assignment.totalMarks;
          if (submission) {
            completedAssignments++;
            if (submission.isLateSubmission) {
              lateSubmissions++;
            }
            if (submission.marksObtained !== undefined) {
              obtainedAssignmentMarks += submission.marksObtained;
            }
          }

          const submissionDoc = submission as any;

          // Calculate percentage and grade
          const percentage =
            submission?.marksObtained === undefined
              ? 0
              : calculatePercentage(
                  submission.marksObtained,
                  assignment.totalMarks,
                );
          const letterGrade =
            submission?.marksObtained === undefined
              ? null
              : percentageToLetterGrade(percentage, true);

          assignmentProgress.push({
            id: assignment.id,
            title: assignment.title,
            totalMarks: assignment.totalMarks,
            deadline: assignment.deadline,
            submitted: !!submission,
            submittedAt: submission?.submittedAt,
            isLateSubmission: submission?.isLateSubmission || false,
            marksObtained: submission?.marksObtained,
            percentage:
              submission?.marksObtained === undefined ? null : percentage,
            letterGrade,
            feedback: submission?.feedback,
            status: submission?.status,
            graded: submission?.isGraded() || false,
            gradedBy: submissionDoc.gradedBy
              ? {
                  id: submissionDoc.gradedBy.id || submissionDoc.gradedBy._id,
                  fullName: submissionDoc.gradedBy.fullName,
                  employeeId: submissionDoc.gradedBy.employeeId,
                }
              : null,
            gradedAt: submission?.gradedAt,
          });
        }

        // Get quizzes for this class
        const quizzes = await Quiz.find({
          classId: classItem._id,
          status: "published",
        });

        const quizProgress = [];
        let totalQuizMarks = 0;
        let obtainedQuizMarks = 0;
        let completedQuizzes = 0;

        for (const quiz of quizzes) {
          const attempts = await QuizAttempt.find({
            quizId: quiz._id,
            studentId: student._id,
            status: { $in: ["submitted", "graded"] },
          })
            .populate("gradedBy", "fullName employeeId")
            .sort({ submittedAt: -1 });

          totalQuizMarks += quiz.totalMarks;
          if (attempts.length > 0) {
            completedQuizzes++;
            const bestAttempt = attempts[0];
            if (bestAttempt.totalMarksObtained !== undefined) {
              obtainedQuizMarks += bestAttempt.totalMarksObtained;
            }
          }

          const attemptDoc = attempts[0] as any;

          // Calculate percentage
          const bestScore = attempts[0]?.totalMarksObtained;
          const percentage =
            bestScore === undefined
              ? 0
              : calculatePercentage(bestScore, quiz.totalMarks);

          quizProgress.push({
            id: quiz.id,
            title: quiz.title,
            totalMarks: quiz.totalMarks,
            deadline: quiz.deadline,
            attempts: attempts.length,
            bestScore,
            percentage: bestScore === undefined ? null : percentage,
            feedback: attempts[0]?.feedback,
            status: attempts[0]?.status,
            graded: attempts[0]?.isGraded() || false,
            gradedBy: attemptDoc.gradedBy
              ? {
                  id: attemptDoc.gradedBy.id || attemptDoc.gradedBy._id,
                  fullName: attemptDoc.gradedBy.fullName,
                  employeeId: attemptDoc.gradedBy.employeeId,
                }
              : null,
            gradedAt: attempts[0]?.gradedAt,
            allAttempts: attempts.map(a => ({
              id: a.id,
              attemptNumber: a.attemptNumber,
              totalMarksObtained: a.totalMarksObtained,
              status: a.status,
              submittedAt: a.submittedAt,
            })),
          });
        }

        // Get attendance for this class
        const attendanceStats = await Attendance.getStatistics(
          classItem._id,
          studentObjectId,
        );

        // Calculate overall grade
        const totalMarks = totalAssignmentMarks + totalQuizMarks;
        const obtainedMarks = obtainedAssignmentMarks + obtainedQuizMarks;
        const overallGradePercentage =
          totalMarks > 0 ? (obtainedMarks / totalMarks) * 100 : 0;
        const overallLetterGrade =
          overallGradePercentage > 0
            ? percentageToLetterGrade(overallGradePercentage, true)
            : null;

        // Calculate assignment average
        const assignmentAveragePercentage =
          completedAssignments > 0 && totalAssignmentMarks > 0
            ? (obtainedAssignmentMarks / totalAssignmentMarks) * 100
            : 0;

        // Calculate quiz average
        const quizAveragePercentage =
          completedQuizzes > 0 && totalQuizMarks > 0
            ? (obtainedQuizMarks / totalQuizMarks) * 100
            : 0;

        // Get primary teacher name
        const teachers = classDoc.teacherIds || [];
        const primaryTeacher =
          Array.isArray(teachers) && teachers.length > 0 ? teachers[0] : null;
        const teacherName =
          primaryTeacher &&
          typeof primaryTeacher === "object" &&
          "fullName" in primaryTeacher
            ? primaryTeacher.fullName
            : "N/A";

        progressData.push({
          class: {
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            courseName: classItem.courseName,
            courseCode: classItem.courseCode || classItem.classCode,
            teacherName,
            semester: classDoc.semester
              ? {
                  id: classDoc.semester.id || classDoc.semester._id,
                  semesterName: classDoc.semester.semesterName,
                  academicYear: classDoc.semester.academicYear,
                }
              : null,
            teachers: Array.isArray(classDoc.teacherIds)
              ? classDoc.teacherIds
              : [],
          },
          assignments: {
            total: assignments.length,
            completed: completedAssignments,
            lateSubmissions,
            progress: assignmentProgress,
            averageGrade: assignmentAveragePercentage,
            averageGradeFormatted: formatPercentage(
              assignmentAveragePercentage,
            ),
          },
          quizzes: {
            total: quizzes.length,
            completed: completedQuizzes,
            progress: quizProgress,
            averageGrade: quizAveragePercentage,
            averageGradeFormatted: formatPercentage(quizAveragePercentage),
          },
          attendance: {
            total: attendanceStats.total,
            present: attendanceStats.present,
            absent: attendanceStats.absent,
            late: attendanceStats.late,
            presentPercentage: attendanceStats.presentPercentage || "0.00",
            absentPercentage: attendanceStats.absentPercentage || "0.00",
            attendancePercentage: attendanceStats.presentPercentage
              ? Number.parseFloat(attendanceStats.presentPercentage)
              : 0,
          },
          overall: {
            totalMarks,
            obtainedMarks,
            overallGrade: overallGradePercentage.toFixed(2),
            overallGradeFormatted: formatPercentage(overallGradePercentage),
            letterGrade: overallLetterGrade,
            completionRate:
              assignments.length + quizzes.length > 0
                ? ((completedAssignments + completedQuizzes) /
                    (assignments.length + quizzes.length)) *
                  100
                : 0,
            classPerformance: overallGradePercentage, // For progress bar
          },
        });
      }

      // Calculate overall statistics
      const overallStats = {
        totalClasses: classes.length,
        totalAssignments: progressData.reduce(
          (sum, p) => sum + p.assignments.total,
          0,
        ),
        completedAssignments: progressData.reduce(
          (sum, p) => sum + p.assignments.completed,
          0,
        ),
        totalQuizzes: progressData.reduce((sum, p) => sum + p.quizzes.total, 0),
        completedQuizzes: progressData.reduce(
          (sum, p) => sum + p.quizzes.completed,
          0,
        ),
        averageGrade:
          progressData.length > 0
            ? progressData.reduce(
                (sum, p) => sum + Number.parseFloat(p.overall.overallGrade),
                0,
              ) / progressData.length
            : 0,
      };

      res.status(200).json({
        success: true,
        message: "Student progress report retrieved successfully",
        data: {
          student: {
            id: student.id,
            fullName: student.fullName,
            studentId: student.studentId,
            academicLevel: student.academicLevel,
            academicYear: student.academicYear,
            email: (student.userId as any)?.email,
          },
          progress: progressData,
          overallStatistics: overallStats,
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get student progress report for teacher error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve progress report",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }
}

export const progressReportController = new ProgressReportController();
